/**
 * IDRA Swap App
 * Main application component
 */

import { h } from 'preact';
import { useState, useCallback, useEffect } from 'preact/hooks';
import { BrowserProvider } from 'ethers';
import { WalletConnect } from './components/WalletConnect';
import { SwapInterface } from './components/SwapInterface';
import { AdminPanel } from './components/AdminPanel';
import { LiquidityPanel } from './components/LiquidityPanel';
import { executeSwap } from './hooks/useSwap';
import {
  dalamBingkai,
  keadaanAwal,
  pasangJembatan,
  pesanGalatJembatan,
  type KeadaanJembatan,
} from './jembatan';

export function App() {
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [provider, setProvider] = useState<BrowserProvider | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [airdropNotification, setAirdropNotification] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string>(window.location.pathname);
  // Вне рамки состояние сразу 'tanpa-induk' → WalletConnect рисуется как раньше, без задержки.
  const [jembatan, setJembatan] = useState<KeadaanJembatan>(() => keadaanAwal());

  // Simple routing - listen to URL changes
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  const handleConnect = useCallback(async (address: string, prov: BrowserProvider) => {
    setWalletAddress(address);
    setProvider(prov);

    const network = await prov.getNetwork();
    setChainId(Number(network.chainId));
  }, []);

  const handleDisconnect = useCallback(() => {
    setWalletAddress(null);
    setProvider(null);
    setChainId(null);
    setStatusMessage('');
    setIsError(false);
    setIsSuccess(false);
    setAirdropNotification(null);
  }, []);

  // Режим рамки: кошелёк держит портал RMS, провайдер приходит через postMessage.
  useEffect(() => {
    if (!dalamBingkai()) return;
    return pasangJembatan(setJembatan);
  }, []);

  // Тот же вход, что и у Web3Modal — useSwap/SwapInterface ничего не знают про мост.
  useEffect(() => {
    if (jembatan.fase !== 'siap') return;
    if (!jembatan.akun) {
      handleDisconnect();
      return;
    }
    // BrowserProvider фиксирует сеть и кеширует signer → пересоздаём на каждое изменение.
    handleConnect(jembatan.akun, new BrowserProvider(jembatan.jembatan as any)).catch((e: any) => {
      setIsError(true);
      setStatusMessage(pesanGalatJembatan(e) ?? `Portal wallet error: ${e?.message ?? e}`);
    });
  }, [jembatan, handleConnect, handleDisconnect]);

  const handleSwap = async (tokenIdentifier: string, amount: string) => {
    if (!provider || !walletAddress || !chainId) return;

    try {
      setIsError(false);
      setIsSuccess(false);
      await executeSwap({
        provider,
        walletAddress,
        token: tokenIdentifier, // Can be "USDT" or a USDC address
        amount,
        chainId,
        onProgress: (msg) => {
          setIsError(false);
          setIsSuccess(false);
          setStatusMessage(msg);
        },
        onAirdrop: (txHash) => {
          setAirdropNotification(`🎉 You received 0.08 POL! Tx: ${txHash.slice(0, 10)}...`);
          setTimeout(() => setAirdropNotification(null), 10000); // Clear after 10s
        }
      });

      // Show success message
      setIsSuccess(true);
      setStatusMessage('🎉 Swap completed successfully!');

      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setStatusMessage('');
        setIsSuccess(false);
      }, 5000);
    } catch (error: any) {
      console.error('Swap error:', error);

      // User-friendly error messages
      let friendlyMessage = 'Transaction failed. Please try again.';

      const dariJembatan = pesanGalatJembatan(error) ?? pesanGalatJembatan(error?.info?.error);

      if (dariJembatan) {
        friendlyMessage = dariJembatan;
      } else if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
        friendlyMessage = 'Transaction cancelled by user.';
      } else if (error.message?.includes('insufficient funds')) {
        friendlyMessage = 'Insufficient funds for transaction.';
      } else if (error.message?.includes('user rejected')) {
        friendlyMessage = 'Transaction rejected.';
      } else if (error.message?.includes('network')) {
        friendlyMessage = 'Network error. Please check your connection.';
      } else if (error.message) {
        // Extract readable part from error message
        const match = error.message.match(/reason="([^"]+)"/);
        if (match) {
          friendlyMessage = match[1] === 'rejected' ? 'Transaction cancelled.' : `Error: ${match[1]}`;
        }
      }

      setIsError(true);
      setIsSuccess(false);
      setStatusMessage(friendlyMessage);

      // Auto-clear error message after 10 seconds
      setTimeout(() => {
        setStatusMessage('');
        setIsError(false);
      }, 10000);
    }
  };

  return (
    <div className="min-h-screen flex flex-col animated-bg">
      {/* Header.

          В рамке шапка НЕ рисуется: логотип и адрес кошелька портал показывает
          сам, а вертикаль внутри рамки дорога — её высоту задаёт родитель и
          подогнать её под содержимое отсюда нельзя (чужой источник).

          Исключение — пока рукопожатие не состоялось. Тогда остаётся тонкая
          строка: без неё человек смотрел бы на обменник без кошелька и без
          единого намёка, почему. Как только фаза стала 'siap', строка уходит. */}
      {jembatan.fase === 'tanpa-induk' ? (
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <img
                src="https://cdn.padigital.id/padigital-logo-light.png"
                alt="Padigital Logo"
                className="h-10"
              />
              {/* Самостоятельный режим — ровно как раньше, через @web3modal/ethers. */}
              <WalletConnect onConnect={handleConnect} onDisconnect={handleDisconnect} />
            </div>
          </div>
        </header>
      ) : jembatan.fase === 'menunggu' ? (
        <div className="bg-white border-b px-4 py-1 text-xs text-gray-500 text-center">
          Menunggu portal…
        </div>
      ) : !jembatan.akun ? (
        <div className="bg-white border-b px-4 py-1 text-xs text-gray-500 text-center">
          Sambungkan dompet di portal
        </div>
      ) : null}

      {/* Airdrop Notification */}
      {airdropNotification && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            {airdropNotification}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex-1 ${
          jembatan.fase === 'tanpa-induk' ? 'py-12' : 'py-3'
        }`}
      >
        {currentPath === '/grahautama' ? (
          // Admin Panel Route
          walletAddress && provider && chainId ? (
            <AdminPanel
              provider={provider}
              walletAddress={walletAddress}
              chainId={chainId}
            />
          ) : (
            <div className="max-w-md mx-auto text-center p-8 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 font-medium">Please connect your wallet to access the admin panel.</p>
            </div>
          )
        ) : currentPath === '/liquid' ? (
          // Liquidity Locks Route (public browse; wallet-gated actions)
          <LiquidityPanel walletAddress={walletAddress} chainId={chainId} />
        ) : (
          // Swap Interface (default route)
          <SwapInterface
            onSwap={handleSwap}
            isConnected={!!walletAddress}
            provider={provider}
            chainId={chainId}
            // В рамке вертикаль дорога: её высоту задаёт портал и подстроиться
            // под содержимое нельзя — чужой источник.
            ringkas={jembatan.fase !== 'tanpa-induk'}
          />
        )}

        {/* Status Message (only for swap page) */}
        {currentPath !== '/grahautama' && currentPath !== '/liquid' && statusMessage && (
          <div className={`mt-6 max-w-md mx-auto p-4 rounded-lg text-center ${
            isError
              ? 'bg-red-50 border border-red-200'
              : isSuccess
              ? 'bg-green-50 border border-green-200'
              : 'bg-blue-50 border border-blue-200'
          }`}>
            <div className="flex items-center justify-center gap-3">
              {!isError && !isSuccess && (
                <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isSuccess && (
                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {isError && (
                <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <p className={`text-sm font-medium ${
                isError
                  ? 'text-red-700'
                  : isSuccess
                  ? 'text-green-700'
                  : 'text-blue-700'
              }`}>
                {statusMessage}
              </p>
            </div>
          </div>
        )}

        {/* Network Info (only for swap page) */}
        {currentPath !== '/grahautama' && currentPath !== '/liquid' && chainId && (
          <div className="mt-6 max-w-md mx-auto text-center text-sm text-gray-500">
            Connected to: {chainId === 137 ? 'Polygon Mainnet' : `Wrong Network (ChainID: ${chainId})`}
          </div>
        )}
      </main>

      {/* Footer. В рамке не рисуется: портал RMS несёт собственный подвал с той
          же правовой строкой, а внутри рамки это ещё ~90 пикселей вертикали,
          которую задаёт родитель и которая не может подстроиться сама. */}
      {jembatan.fase === 'tanpa-induk' && (
        <footer className="bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-sm text-gray-500">
            <p>© 2026 Padigital. All rights reserved.</p>
            <p className="mt-2">Powered by Polygon Network</p>
          </div>
        </footer>
      )}
    </div>
  );
}
